import noop from "lodash/noop";
import { MMKV } from "react-native-mmkv";

import { captureException } from "@/services/AppMonitor";
import { isWeb } from "@/utils/platform.util";

const WebStorageKeyPrefix = "mmkv:";

// eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- web shim intentionally mimics MMKV interface without full implementation
const WebUnencryptedStorage = {
  addOnValueChangedListener: (_cb: (key: string) => void) => ({
    remove: noop,
  }),
  clearAll: () => {
    Object.keys(globalThis.localStorage)
      .filter((k) => k.startsWith(WebStorageKeyPrefix))
      .forEach((k) => globalThis.localStorage.removeItem(k));
  },
  contains: (key: string) =>
    globalThis.localStorage.getItem(WebStorageKeyPrefix + key) !== null,
  delete: (key: string) => {
    globalThis.localStorage.removeItem(WebStorageKeyPrefix + key);
  },
  getAllKeys: () =>
    Object.keys(globalThis.localStorage)
      .filter((k) => k.startsWith(WebStorageKeyPrefix))
      .map((k) => k.slice(WebStorageKeyPrefix.length)),
  getBoolean: (key: string) => {
    const v = globalThis.localStorage.getItem(WebStorageKeyPrefix + key);
    if (v === null) return undefined;
    return v === "true";
  },
  getNumber: (key: string) => {
    const v = globalThis.localStorage.getItem(WebStorageKeyPrefix + key);
    if (v === null) return undefined;
    const n = Number(v);
    return Number.isNaN(n) ? undefined : n;
  },
  getString: (key: string) => {
    const v = globalThis.localStorage.getItem(WebStorageKeyPrefix + key);
    return v === null ? undefined : v;
  },
  // eslint-disable-next-line max-params, sonarjs/use-type-alias -- matches MMKV.set(key, value) signature
  set: (key: string, value: boolean | number | string) => {
    globalThis.localStorage.setItem(WebStorageKeyPrefix + key, String(value));
  },
} as unknown as MMKV;

// MMKV requires JSI (synchronous native calls). In environments where JSI is
// unavailable (Storybook web, remote JS debugger), `new MMKV()` throws a JS Error.
// We catch it and fall back to a plain in-memory store so the module can load safely.
const createMMKVStorage = (): MMKV => {
  if (isWeb()) return WebUnencryptedStorage;

  try {
    return new MMKV();
  } catch {
    const store = new Map<string, boolean | number | string>();
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    return {
      addOnValueChangedListener: (_cb: (key: string) => void) => ({
        remove: noop,
      }),
      clearAll: () => {
        store.clear();
      },
      contains: (key: string) => store.has(key),
      delete: (key: string) => {
        store.delete(key);
      },
      getAllKeys: () => Array.from(store.keys()),
      getBoolean: (key: string) => {
        const v = store.get(key);
        return typeof v === "boolean" ? v : undefined;
      },
      getNumber: (key: string) => {
        const v = store.get(key);
        return typeof v === "number" ? v : undefined;
      },
      getString: (key: string) => {
        const v = store.get(key);
        return typeof v === "string" ? v : undefined;
      },
      // eslint-disable-next-line max-params
      set: (key: string, value: boolean | number | string) => {
        store.set(key, value);
      },
    } as unknown as MMKV;
  }
};

const MmkvStorage = createMMKVStorage();

enum UnencryptedStorageKey {
  AppInstallation = "app_installation",
  DownloadEnforcementDismissedDate = "download_enforcement_dismissed_date",
  HasInteractedWithSMTFeature = "has_interacted_with_smt_feature",
  HasRespondedToPushNotificationPrompt = "has_responded_to_push_notification_prompt",
  HomeTabRecommendedArticleDismissed = "home_tab_recommended_article_dismissed",
  HomeTabRecommendedFAQDismissed = "home_tab_recommended_faq_dismissed",
  HomeTabTestimonalDismissed = "home_tab_testimonial_dismissed",
  IsBiometricAuthEnabled = "is_biometric_auth_enabled",
  IsRememberMeEnabled = "is_remember_me_enabled",
  OnboardingNotificationsDismissed = "onboarding_notifications_dismissed",
  RecommendedGroupClassNoneOfTheseTimes = "recommended_group_class_none_of_these_times",
  RecommendedGroupClassRemindMeLaterDate = "recommended_group_class_remind_me_later_date",
  StorybookExample = "storybook_example",
  UserDismissedConsent = "user_dismissed_consent",
  WaitingRoomReservation = "waiting_room_reservation",
  WebRedesignOverviewDismissed = "web_redesign_overview_dismissed",
}

type RecommendedGroupClassStorageKey =
  | `${UnencryptedStorageKey.RecommendedGroupClassNoneOfTheseTimes}_${string}_${string}`
  | `${UnencryptedStorageKey.RecommendedGroupClassRemindMeLaterDate}_${string}_${string}`;

type WebRedesignOverviewStorageKey =
  `${UnencryptedStorageKey.WebRedesignOverviewDismissed}_${string}`;

type DownloadEnforcementStorageKey =
  `${UnencryptedStorageKey.DownloadEnforcementDismissedDate}_${string}`;

// The LaunchDarkly SDK persists its flag cache under dynamically-namespaced keys, all
// prefixed with `LaunchDarkly_`. Kept as a standalone prefix (not a UnencryptedStorageKey
// member) so the bare "LaunchDarkly" key is never a valid standalone key.
// See crossPlatform/LaunchDarkly/ttlStorage.native.ts.
const LaunchDarklyStorageKeyPrefix = "LaunchDarkly_";

type LaunchDarklyStorageKey = `LaunchDarkly_${string}`;

type UnencryptedStorageKeyInput =
  | DownloadEnforcementStorageKey
  | LaunchDarklyStorageKey
  | RecommendedGroupClassStorageKey
  | UnencryptedStorageKey
  | WebRedesignOverviewStorageKey;

const getUnencryptedStringValue = (
  key: UnencryptedStorageKeyInput,
): string | undefined => {
  try {
    return MmkvStorage.getString(key);
  } catch (caughtErr) {
    captureException(caughtErr);
    return undefined;
  }
};

const getUnencryptedBooleanValue = (
  key: UnencryptedStorageKeyInput,
): boolean => {
  const stringValue = getUnencryptedStringValue(key);
  return stringValue === "true";
};

const saveUnencryptedStringValue = ({
  key,
  value,
}: {
  key: UnencryptedStorageKeyInput;
  value: string;
}): void => {
  try {
    MmkvStorage.set(key, value);
  } catch (caughtErr) {
    captureException(caughtErr);
  }
};

const saveUnencryptedBooleanValue = ({
  isTrue,
  key,
}: {
  isTrue: boolean;
  key: UnencryptedStorageKeyInput;
}): void => {
  saveUnencryptedStringValue({ key, value: isTrue ? "true" : "false" });
};

const deleteUnencryptedValue = (key: UnencryptedStorageKeyInput): void => {
  try {
    MmkvStorage.delete(key);
  } catch (caughtErr) {
    captureException(caughtErr);
  }
};

// UnencryptedStorage is meant for storing values that are NOT sensitive in nature.
// For example: storing whether a piece of content has been permanently dismissed, etc.
//
// This provides improved performance, reduced complexity, and improved battery life,
// over using Encyrypted (or Secure) storage,
// especially in cases where a value may be often re-retrieved from storage.
const useUnencryptedStorage = () => {
  // NOTE: per-entry size limit is roughly 2 MB as a hard limit; multiGet/multiSet can be used to chunk larger values into multiple storage key/value pairs
  // NOTE: total storage size is capped at 6 MB by default, and can be increased with a feature flag
  // See: https://react-native-async-storage.github.io/async-storage/docs/limits/
  return {
    deleteUnencryptedValue,
    getUnencryptedBooleanValue,
    getUnencryptedStringValue,
    saveUnencryptedBooleanValue,
    saveUnencryptedStringValue,
  };
};

export {
  deleteUnencryptedValue,
  getUnencryptedBooleanValue,
  getUnencryptedStringValue,
  LaunchDarklyStorageKeyPrefix,
  MmkvStorage,
  saveUnencryptedBooleanValue,
  saveUnencryptedStringValue,
  UnencryptedStorageKey,
  useUnencryptedStorage,
};

export type {
  DownloadEnforcementStorageKey,
  LaunchDarklyStorageKey,
  RecommendedGroupClassStorageKey,
  WebRedesignOverviewStorageKey,
};
